import { useState, useEffect, useCallback } from 'react';
import {
  useParams,
  useNavigate,
  generatePath,
  useLocation,
} from 'react-router-dom';
import { useForm } from 'react-hook-form';
import isEqual from 'lodash.isequal';
import {
  SidePanel,
  SidePanelType,
  ButtonPrimary,
  ButtonSecondary,
  PermissionEnum,
  Popup,
  Tabs,
  SidePanelLoader,
  responseErrorsResolver,
  ResponseError,
  ChangeLogs,
  PermissionDenied,
  getResponseErrors,
} from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import {
  Schedule,
  WeekDays,
  AutoExchangeFormValues,
  Period,
  PrimaryAccount,
} from './types';
import AutoSettingsForm from '../../components/auto-settings/form';
import {
  useLazyGetExchangeAutoSettingsDefaultQuery,
  useUpdateExchangeAutoSettingsPairMutation,
  useCreateExchangeAutoSettingsPairMutation,
  useLazyGetRuleChangeLogsQuery,
  useLazyGetAutoExchangeAccountsCategoriesQuery,
} from './store/services';
import { clearDefaultAutoSettingsDetailsState } from './store/slices/sidepanel';
import useGetAccountsAutocompleteList from './hooks/use-get-accounts-list';

import ROUTES from '../../routes';

import cn from './auto-settings.module.css';

const AutoSettingsGeneral = () => {
  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const [schedule, setSchedule] = useState<Schedule[]>([
    {
      weekday: WeekDays.MONDAY,
      active: false,
      timerange: [
        {
          id: 'range-0',
          start: '',
          end: '',
        },
      ],
    },
    {
      weekday: WeekDays.TUESDAY,
      active: false,
      timerange: [
        {
          id: 'range-0',
          start: '',
          end: '',
        },
      ],
    },
    {
      weekday: WeekDays.WEDNESDAY,
      active: false,
      timerange: [
        {
          id: 'range-0',
          start: '',
          end: '',
        },
      ],
    },
    {
      weekday: WeekDays.THURSDAY,
      active: false,
      timerange: [
        {
          id: 'range-0',
          start: '',
          end: '',
        },
      ],
    },
    {
      weekday: WeekDays.FRIDAY,
      active: false,
      timerange: [
        {
          id: 'range-0',
          start: '',
          end: '',
        },
      ],
    },
    {
      weekday: WeekDays.SATURDAY,
      active: false,
      timerange: [
        {
          id: 'range-0',
          start: '',
          end: '',
        },
      ],
    },
    {
      weekday: WeekDays.SUNDAY,
      active: false,
      timerange: [
        {
          id: 'range-0',
          start: '',
          end: '',
        },
      ],
    },
  ]);
  const [dirtyPopupVisible, setDirtyPopupVisible] = useState(false);
  const [activeTab, setActiveTab] = useState<'details' | 'log'>('details');
  const [weekdaysWithErrors, setWeekdaysWithErrors] = useState<WeekDays[]>([]);
  const [globalError, setGlobalError] = useState('');
  const [defaultSchedule, setDefaultSchedule] = useState<Schedule[]>([]);
  const [selectedPrimaryAccounts, setSelectedPrimaryAccounts] = useState<
    string[]
  >([]);
  const [primaryAccountsList, setPrimaryAccountsList] = useState<
    PrimaryAccount[]
  >([]);

  const dispatch = useAppDispatch();

  const {
    autoSettings: {
      details: {
        defaultSettings,
        changeLogs,
        changeLogsPagination,
        accountCategories,
      },
    },
    administrationDictionaries: { users },
    exchangeDictionaries: { activePermissions, currencies, rateSource },
  } = useAppSelector((state) => state);

  const [getDefaultSettings, { isLoading, isFetching }] =
    useLazyGetExchangeAutoSettingsDefaultQuery();
  const [update, { isLoading: requestInProgress }] =
    useUpdateExchangeAutoSettingsPairMutation();
  const [create, { isLoading: createInProgress }] =
    useCreateExchangeAutoSettingsPairMutation();
  const [
    getChangeLogs,
    { isLoading: changeLogsIsLoading, isFetching: changeLogsIsFetching },
  ] = useLazyGetRuleChangeLogsQuery();
  const [getAccountsCategories, { isLoading: accountsCategoriesLoading }] =
    useLazyGetAutoExchangeAccountsCategoriesQuery();

  const { currency } = useParams();

  const {
    control,
    reset,
    formState: { errors, isDirty },
    handleSubmit,
    setError,
    watch,
    setValue,
  } = useForm<AutoExchangeFormValues>();

  const formIsDirty = isDirty || !isEqual(schedule, defaultSchedule);

  const fields = watch();
  const accountsType = watch('accountsType');
  const createChildrenTransactions = watch('createChildrenTransactions');

  const navigate = useNavigate();
  const { search } = useLocation();

  const getChangeLogsAction = useCallback(
    (cursor?: string) => {
      getChangeLogs(
        `?take=20&entityType=EXCHANGE_AUTO_RULE&entityId=${defaultSettings?.id}${
          cursor ? `&cursor=${cursor}` : ''
        }`,
      );
    },
    [defaultSettings],
  );

  const {
    autocompleteList: fromAccounts,
    onSearch: onSearchFromAccountsList,
    isLoading: fromAccountsListIsLoading,
    nextPageIsLoading: fromAccountsNextPageIsLoading,
    onScroll: onScrollFromAccounts,
    pagination: fromAccountsPagination,
    getAccountBytId: getFromAccountById,
  } = useGetAccountsAutocompleteList();

  const {
    autocompleteList: toAccounts,
    onSearch: onSearchToAccountsList,
    isLoading: toAccountsListIsLoading,
    nextPageIsLoading: toAccountsNextPageIsLoading,
    onScroll: onScrollToAccounts,
    pagination: toAccountsPagination,
    getAccountBytId: getToAccountById,
  } = useGetAccountsAutocompleteList();

  const onSearchAutocomplete = (name: string, searchValue: string) => {
    if (name === 'fromAccount') {
      onSearchFromAccountsList(searchValue);
    } else if (name === 'toAccount') {
      onSearchToAccountsList(searchValue);
    }
  };

  const onSubmit = handleSubmit(async (values) => {
    let updateIsAvailable = true;
    let response;

    setGlobalError('');

    const periods: Period[] = [];
    const activeWeekDays: WeekDays[] = [];
    const weekdaysErrors: WeekDays[] = [];

    const body: AutoExchangeFormValues = {
      rateSourceLifetimeSeconds: Number(values.rateSourceLifetimeSeconds),
      primaryAccountIds: values.primaryAccountIds,
      createChildrenTransactions: values.createChildrenTransactions,
      accountsType: values.accountsType,
      fromAccountId: values.fromAccountId || null,
      toAccountId: values.toAccountId || null,
      rateSourceId: values.rateSourceId || null,
    };

    if (!isEqual(schedule, defaultSchedule)) {
      schedule?.forEach((item) => {
        if (item.active) {
          activeWeekDays.push(item.weekday);

          const ranges = item.timerange?.filter(
            (period) => period.start && period.end,
          );

          if (!ranges.length) {
            weekdaysErrors.push(item.weekday);
          } else {
            ranges?.forEach((range) => {
              const rangeBody: {
                weekDay: WeekDays;
                startTime: string;
                endTime: string;
                id?: string;
              } = {
                weekDay: item.weekday,
                startTime: range.start,
                endTime: range.end,
              };

              if (!range.id.includes('range')) {
                rangeBody.id = range.id;
              }

              periods.push(rangeBody);
            });
          }
        }
      });

      body.activeWeekDays = activeWeekDays;
      body.periods = periods;
    }

    if (weekdaysErrors.length) {
      updateIsAvailable = false;
      setWeekdaysWithErrors(weekdaysErrors);
    } else {
      setWeekdaysWithErrors([]);
    }

    if (updateIsAvailable) {
      if (defaultSettings && defaultSettings.id) {
        response = (await update({
          id: defaultSettings?.id,
          body,
        })) as {
          error: ResponseError | { data: { message: string } };
          data?: { data: AutoExchangeFormValues };
        };
      } else {
        body.active = true;

        response = (await create(body)) as {
          error: ResponseError | { data: { message: string } };
          data?: { data: AutoExchangeFormValues };
        };
      }

      if (response) {
        if (response?.error) {
          responseErrorsResolver({
            errors: response.error as ResponseError,
            setError,
            setGlobalError,
          });
        } else {
          // eslint-disable-next-line no-lonely-if
          if (!defaultSettings) {
            const defaultSettingsResponse = (await getDefaultSettings()) as {
              error?: ResponseError;
            };

            if (defaultSettingsResponse?.error) {
              getResponseErrors({
                error: defaultSettingsResponse.error,
                setErrorList: setGlobalError,
              });
            }
          } else {
            setDefaultSchedule(schedule);

            reset({
              ...fields,
              ...body,
            });
          }
        }
      }
    }
  });

  const handleClose = () => {
    setPanelIsOpen(false);
    setDirtyPopupVisible(false);
    reset({});
    dispatch(clearDefaultAutoSettingsDetailsState());

    const url = generatePath(ROUTES.AUTO_SETTINGS_CURRENCY, {
      currency: currency as string,
    });

    setTimeout(() => {
      navigate(`${url}${search || ''}`);
    }, 100);
  };

  const getDefaultSettingsRequestAction = useCallback(async () => {
    const response = (await getDefaultSettings()) as {
      error?: ResponseError;
    };

    if (response?.error) {
      getResponseErrors({
        error: response.error,
        setErrorList: setGlobalError,
      });
    }
  }, []);

  useEffect(() => {
    setPanelIsOpen(true);
    getDefaultSettingsRequestAction();
  }, []);

  useEffect(() => {
    if (
      activePermissions?.includes(PermissionEnum.ACCOUNTS_CHART) &&
      !accountCategories
    ) {
      getAccountsCategories('all=true&include=primaryAccounts');
    }
  }, [activePermissions, accountCategories]);

  useEffect(() => {
    if (accountCategories) {
      const primaryAccounts: PrimaryAccount[] = [];

      accountCategories?.forEach((item) => {
        item.accountGroups?.forEach((group) => {
          group?.primaryAccounts?.forEach((primary) => {
            if (!primaryAccounts.find((account) => account.id === primary.id)) {
              primaryAccounts.push(primary);
            }
          });
        });
      });

      setPrimaryAccountsList(primaryAccounts);
    }
  }, [accountCategories]);

  useEffect(() => {
    if (
      activeTab === 'log' &&
      !changeLogs &&
      activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST)
    ) {
      getChangeLogsAction();
    }
  }, [activeTab]);

  useEffect(() => {
    if (defaultSettings) {
      reset({
        rateSourceLifetimeSeconds: defaultSettings.rateSourceLifetimeSeconds,
        accountsType: defaultSettings?.accountsType,
        fromAccountId: defaultSettings?.fromAccountId || '',
        toAccountId: defaultSettings?.toAccountId || '',
        rateSourceId: defaultSettings?.rateSourceId || '',
        primaryAccountIds: defaultSettings?.primaryAccountIds,
        createChildrenTransactions: defaultSettings.createChildrenTransactions,
      });

      let nextSchedule = ([] as Schedule[]).concat(schedule);

      if (defaultSettings?.primaryAccountIds) {
        setSelectedPrimaryAccounts(defaultSettings?.primaryAccountIds);
      }

      if (defaultSettings?.activeWeekDays?.length) {
        nextSchedule = nextSchedule?.map((item) => {
          const result = { ...item };

          if (
            defaultSettings.activeWeekDays?.find(
              (weekday) => weekday === item.weekday,
            )
          ) {
            result.active = true;
          }

          return result;
        });

        if (defaultSettings?.periods?.length) {
          nextSchedule = nextSchedule?.map((item) => {
            const result = { ...item };

            const currentWeekdayTimerange = defaultSettings?.periods?.filter(
              (period) => period.weekDay === item.weekday,
            );

            let nextTimeRange = (
              [] as { id: string; start: string; end: string }[]
            ).concat(result.timerange);

            if (currentWeekdayTimerange?.length) {
              nextTimeRange = currentWeekdayTimerange?.map((period) => ({
                id: period.id as string,
                start: period.startTime,
                end: period.endTime,
              }));
            }

            result.timerange = nextTimeRange;

            return result;
          });
        }

        setSchedule(nextSchedule);
        setDefaultSchedule(nextSchedule);
      } else {
        setDefaultSchedule(schedule);
      }
    }
  }, [defaultSettings]);

  useEffect(() => {
    if (defaultSettings) {
      if (defaultSettings?.fromAccountId) {
        const currentAccount = fromAccounts?.find(
          (item) => item.value === defaultSettings?.fromAccountId,
        );

        if (!currentAccount) {
          getFromAccountById(defaultSettings?.fromAccountId);
        }
      }

      if (defaultSettings?.toAccountId) {
        const currentAccount = toAccounts?.find(
          (item) => item.value === defaultSettings?.toAccountId,
        );

        if (!currentAccount) {
          getToAccountById(defaultSettings?.toAccountId);
        }
      }
    }
  }, [defaultSettings]);

  let content;

  if (isLoading || isFetching) {
    content = <SidePanelLoader />;
  } else {
    content = (
      <>
        <div className={cn.settingsTabsWrapper}>
          {defaultSettings && (
            <Tabs
              tabs={[
                {
                  name: 'Details',
                  value: 'details',
                },
                {
                  name: 'Change log',
                  value: 'log',
                },
              ]}
              activeTab={activeTab}
              onClick={(tab) => setActiveTab(tab as 'details' | 'log')}
            />
          )}
        </div>
        {activeTab === 'details' && (
          <AutoSettingsForm
            control={control}
            errors={errors}
            currencies={currencies}
            schedule={schedule}
            setSchedule={setSchedule}
            isEditable={Boolean(
              activePermissions?.includes(
                PermissionEnum.SETTINGS_AUTO_EXCHANGE_UPDATE_ACTION,
              ),
            )}
            weekdaysWithErrors={weekdaysWithErrors}
            accountCategories={accountCategories}
            accountsCategoriesLoading={accountsCategoriesLoading}
            accountsType={accountsType}
            selectedPrimaryAccounts={selectedPrimaryAccounts}
            setSelectedPrimaryAccounts={setSelectedPrimaryAccounts}
            primaryAccountsList={primaryAccountsList}
            setValue={setValue}
            createSyncedTransaction={createChildrenTransactions}
            fromAccounts={fromAccounts}
            fromAccountsListIsLoading={fromAccountsListIsLoading}
            fromAccountsNextPageIsLoading={fromAccountsNextPageIsLoading}
            onScrollFromAccounts={onScrollFromAccounts}
            fromAccountsPagination={fromAccountsPagination}
            toAccounts={toAccounts}
            toAccountsListIsLoading={toAccountsListIsLoading}
            toAccountsNextPageIsLoading={toAccountsNextPageIsLoading}
            onScrollToAccounts={onScrollToAccounts}
            toAccountsPagination={toAccountsPagination}
            onSearchAutocomplete={onSearchAutocomplete}
            rateSource={rateSource?.map((item) => ({
              name: item.name,
              value: item.id,
              description: item.description,
            }))}
            general
          />
        )}
        {activeTab === 'log' && (
          <>
            {activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST) && (
              <ChangeLogs
                entityType="EXCHANGE_AUTO_RULE"
                entityId={defaultSettings?.id as string}
                changeLogsIsLoading={changeLogsIsLoading}
                changeLogs={changeLogs}
                activePermission={activePermissions}
                users={users}
                pagination={changeLogsPagination}
                changeLogsIsFetching={changeLogsIsFetching}
                getChangeLogs={getChangeLogsAction}
              />
            )}
            {!activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST) && (
              <PermissionDenied />
            )}
          </>
        )}
      </>
    );
  }

  return (
    <>
      <SidePanel
        type={SidePanelType.AML}
        closeOnClickOutside
        isOpen={panelIsOpen}
        errorSidepanel={globalError}
        onClose={() => {
          if (formIsDirty) {
            setDirtyPopupVisible(true);
          } else {
            handleClose();
          }
        }}
        title="General Settings"
        sidepanelExpandedKey="auto-settings-panel"
        className={cn.rateDetailsSidepanel}
        footer={
          <>
            {activePermissions?.includes(
              PermissionEnum.SETTINGS_AUTO_EXCHANGE_UPDATE_ACTION,
            ) && (
              <ButtonPrimary
                text={
                  requestInProgress || createInProgress ? 'Loading...' : 'Save'
                }
                onClick={onSubmit}
                disabled={!formIsDirty || requestInProgress || createInProgress}
                bold
              />
            )}

            <ButtonSecondary
              text="Close"
              onClick={() => {
                if (formIsDirty) {
                  setDirtyPopupVisible(true);
                } else {
                  handleClose();
                }
              }}
              className="button-white"
              bold
            />
          </>
        }
      >
        {content}
      </SidePanel>
      <Popup
        title="You have unsaved changes"
        description=""
        onCancelClick={() => setDirtyPopupVisible(false)}
        onOkClick={handleClose}
        cancelButtonText="Cancel"
        okButtonText="Close"
        isVisible={dirtyPopupVisible}
        parent={document.getElementById('dirty-popup') as HTMLElement}
      />
      <div id="dirty-popup" />
    </>
  );
};

export default AutoSettingsGeneral;
